export interface Settings {
    general?: GeneralSettings;
    trustbox?: TrustBoxSettings;
    pastOrders?: PastOrdersInfo;
    pluginVersion?: string;
    gtinSelector?: string;
    mpnSelector?: string;
    skuSelector?: string;
    productIdentificationOptions?: string[];
    source?: string;
    version?: string;
    isFromMarketplace?: string;
    stores?: any;
    shop?: string;
    stencilEnabled?: boolean;
    engagement?: EnagagementData;
    configurationScopeTree?: ConfigurationScope[];
}
export interface EnagagementData {
    date: string;
    source?: string;
    shop?: string;
}
export interface ConfigurationScopeNames {
    site: string;
    store: string;
    view: string;
}
export interface ConfigurationScope {
    names: ConfigurationScopeNames;
    ids: string[];
    domain: string;
}

export interface CustomTemplateParams {
    templateId: string;
    languageId: string;
    scopeTree: ConfigurationScope;
}

export interface IntegrationConfig {
    pluginSettings?: Settings;
    key?: string;
    enableProductReviews: boolean;
    templateMap?: CustomTemplateParams[];
    allowedDomains?: string[];
}

export interface GeneralSettings {
    key?: string;
    invitationTrigger?: InvitationTrigger;
    mappedInvitationTrigger?: string[];
}

export interface TrustBoxEntitySettings {
    enabled?: TrustBoxToggle;
    snippet?: string;
    customizations?: string;
    page?: string;
    position?: TrustBoxPosition;
    xpaths?: string;
    corner?: TrustBoxCorner;
    paddingx?: string;
    paddingy?: string;
    sku?: string;
    name?: string;
    uuid?: string;
    error?: any;
    widgetName?: string;
}

export interface PastOrdersInfo {
    showInitial?: boolean;
    syncInProgress?: boolean;
    synced?: number;
    unsynced?: number;
    failed?: FailedOrders[];
    total?: number;
    analyze?: PastOrdersAnalyze;
}

export interface ButtonTrackingProps {
    action: string;
    placement: string;
    name: string;
    source: string;
    isFromMarketplace?: string;
    tierName: string;
    mode?: string;
    value?: string;
    valid?: string;
    changed?: string[];
}

export interface ToggleTrackingProps {
    action: string;
    placement: string;
    name: string;
    source: string;
    isFromMarketplace?: string;
    tierName: string;
}

export interface LinkTrackingProps {
    target: string;
    placement: string;
    name: string;
    source: string;
    tierName: string;
}
export interface TrustBoxSettings {
    trustboxes: TrustBoxEntitySettings[];
    pageUrls?: PageUrls;
    activeTrustbox?: number;
    dirty?: boolean;
}

export enum TrustBoxPage {
    Product = 'product',
    Landing = 'landing',
    Category = 'category',
}

export enum TrustBoxToggle {
    Enabled = 'enabled',
    Disabled = 'disabled',
}

export enum TrustBoxPosition {
    Above = 'before',
    Below = 'after',
    Floating = 'floating',
}

export enum EmailImageType {
    PastOrders,
    ProductReviews,
}

export enum EmailType {
    ServiceReviews,
    ProductReviews,
}

export enum EmailSentFrom {
    PastOrders = 'pastOrders',
    ProductReviews = 'productReviews',
}

export enum InvitationTrigger {
    OrderConfirmed = 'orderConfirmed',
    OrderCompleted = 'orderCompleted',
}

export const InvitationTriggerMap = {
    woocommerce: {
        orderConfirmed: ['processing', 'trustpilotOrderConfirmed'],
        orderCompleted: ['completed'],
        tooltip: 'general.woocommerceTooltip',
    },
    prestashop: {
        orderConfirmed: ['2', 'trustpilotOrderConfirmed'],
        orderCompleted: ['4', '5'],
        tooltip: 'general.prestashopTooltip',
    },
    opencart: {
        orderConfirmed: ['2', '15', 'trustpilotOrderConfirmed'],
        orderCompleted: ['3', '5'],
        tooltip: 'general.opencartTooltip',
    },
    magento1: {
        orderConfirmed: ['processing', 'trustpilotOrderConfirmed'],
        orderCompleted: ['complete'],
        tooltip: 'general.magentoTooltip',
    },
    magento2: {
        orderConfirmed: ['processing', 'trustpilotOrderConfirmed'],
        orderCompleted: ['complete'],
        tooltip: 'general.magentoTooltip',
    },
    shopify: {
        orderConfirmed: ['orders/paid', 'trustpilotOrderConfirmed'],
        orderCompleted: ['orders/fulfilled'],
        tooltip: 'general.shopifyTooltip',
    },
    bigcommerce: {
        orderConfirmed: ['8', '9', '11', 'trustpilotOrderConfirmed'],
        orderCompleted: ['2', '10'],
        tooltip: 'general.bigcommerceTooltip',
    },
    shopware: {
        orderConfirmed: ['0', '1', 'trustpilotOrderConfirmed'],
        orderCompleted: ['2', '7'],
        tooltip: 'general.shopwareTooltip',
    },
};

export interface FailedOrders {
    referenceId: string;
    error: string;
}

export interface PageUrls {
    landing: string;
    category: string;
    product: string;
    [key: string]: string;
}

export enum TrustBoxCorner {
    'Bottom-left corner'  = 'bottom: #{Y}px; left:  #{X}px;',
    'Bottom-right corner' = 'bottom: #{Y}px; right: #{X}px;',
    'Top-left corner'     = 'top:    #{Y}px; left:  #{X}px;',
    'Top-right corner'    = 'top:    #{Y}px; right: #{X}px;',
}

export interface Version {
    source: string;
    version: string;
    date: string;
    notes: string[];
}

export interface InvitationsSettings {
    senderName: string;
    delayInDays: Period;
    landingPageLocale: Language;
    replyToEmail: string;
    templateId: string;
    website?: string;
    verifiedReplyToEmailAddresses: string[];
    useSeparateProductReviewInvitations: boolean;
    productReviewInvitationDelayInDays: Period;
    productReviewInvitationTemplateId: string;
}

export enum Period {
    '0 days'  = '0',
    '1 day'   = '1',
    '2 days'  = '2',
    '3 days'  = '3',
    '4 days'  = '4',
    '5 days'  = '5',
    '6 days'  = '6',
    '7 days'  = '7',
    '2 weeks' = '14',
    '3 weeks' = '21',
    '4 weeks' = '28',
    '6 weeks' = '42',
    '2 months'= '60',
    '3 months'= '90',
}

export enum Language {
    'Danish' = 'da-DK',
    'Dutch' = 'nl-NL',
    'English' = 'en-US',
    'Finnish' = 'fi-FI',
    'French' =  'fr-FR',
    'German' = 'de-DE',
    'Italian' = 'it-IT',
    'Japanese' = 'ja-JP',
    'Mandarin Chinese' = 'zh-CN',
    'Norwegian' = 'nb-NO',
    'Polish' = 'pl-PL',
    'Portuguese' = 'pt-PT',
    'Portuguese (Brazil)' = 'pt-BR',
    'Russian' = 'ru-RU',
    'Spanish' = 'es-ES',
    'Swedish' = 'sv-SE',
}

export enum DefaultWebsite {
    'All Websites' =  'default',
}

export interface AuthResponse {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
}

export interface Consent {
    id?: string;
    isAccepted: boolean;
    type: ConsentType;
    unitId: string;
    version: number;
}

export enum ConsentType {
    Ecomm = 'Ecomm',
}

export interface ConsentSpecification {
    id?: string;
    text: string;
    type: ConsentType;
    locale: string;
    version: number;
    title: string;
}

export enum PreviewModalOverlay {
    GetSnippet,
    SelectPosition,
    CustomizeTrustbox,
    None
}

export interface BusinessUser {
    id: string;
    name: string;
    email: string;
    locale: string;
}

export interface AnalyticsUser {
    id: string;
}

export interface BusinessUnit {
    id: string;
    name?: BusinessUnitNames;
    displayName?: string;
    isActive?: boolean;
}

export interface BusinessUnitNames {
    referring?: string[];
    identifying: string;
}

export interface ConsentResponse {
    consentIsAccepted: boolean;
    businessUnitId: string;
}

export interface ReleasesResponse {
    version: string;
    latestVersion: boolean;
    minVersion?: boolean;
    date: Date;
    notes: string[];
}

export interface FaqsResponse {
    title: string;
    paragraph: string;
}

export interface CheckIntegrationKeyResponse {
    exists: boolean;
}

export interface Template {
    id: string;
    name: string;
    locale: Language;
    isDefaultTemplate: boolean;
}

export interface TierFeatures {
    features: string[];
}

export enum TierFeature {
    Reviews_ProductReviews = 'Reviews_ProductReviews',
}

export enum LandingPageLocales {
    'www.trustpilot.com' = 'en-US',
    'at.trustpilot.com' = 'de-AT',
    'au.trustpilot.com' = 'en-AU',
    'br.trustpilot.com' = 'pt-BR',
    'ca.trustpilot.com' = 'en-CA',
    'ch.trustpilot.com' = 'de-CH',
    'cn.trustpilot.com' = 'zh-CN',
    'de.trustpilot.com' = 'de-DE',
    'dk.trustpilot.com' = 'da-DK',
    'es.trustpilot.com' = 'es-ES',
    'fi.trustpilot.com' = 'fi-FI',
    'fr-be.trustpilot.com' = 'fr-BE',
    'fr.trustpilot.com' = 'fr-FR',
    'ie.trustpilot.com' = 'en-IE',
    'it.trustpilot.com' = 'it-IT',
    'jp.trustpilot.com' = 'ja-JP',
    'nl-be.trustpilot.com' = 'nl-BE',
    'nl.trustpilot.com' = 'nl-NL',
    'no.trustpilot.com' = 'nb-NO',
    'nz.trustpilot.com' = 'en-NZ',
    'pl.trustpilot.com' = 'pl-PL',
    'pt.trustpilot.com' = 'pt-PT',
    'ru.trustpilot.com' = 'ru-RU',
    'se.trustpilot.com' = 'sv-SE',
    'uk.trustpilot.com' = 'en-GB',
}

export enum PastOrdersPeriod {
    '1 week' = '7',
    '2 weeks' = '14',
    '1 month' = '30',
    '3 months' = '90',
    '6 months' = '180',
}

export interface PastOrders {
    shops: string[];
    period: string;
}

export enum TooltipIconType {
    ripple,
    questionMark
}

export interface TooltipInterpolations {
    [key: string]: string;
}

export interface TooltipSettings {
    text: string;
    type?: TooltipType;
    iconType?: TooltipIconType;
    interpolations?: TooltipInterpolations;
}

export enum TooltipType {
    text,
    html
}

export enum UpdateMode {
    DownloadZip,
    AutomaticUpdate
}

export enum UpdateType {
    Critical,
    Minor,
    None
}

export interface Tier {
    links: {
        href: string;
        method: string;
        rel: string;
    };
    name: string;
    id: string;
    isDefaultTier: string;
}

export interface Widget {
    name: string;
    id: string;
    available: boolean;
    screenshot: string;
    mockup: string;
    baseSnippet: string;
    customizations?: Customizations;
    defaults?: {
        width: string;
        height: string;
    };
    features: string[];
}

export interface Customizations {
    width: { attributeName: string; };
    height: { attributeName: string; };
    stars: { attributeName: string; options?: number[]; };
    theme: { attributeName: string; options: string[]; };
    productName: { attributeName: string } | false;
    sku: { attributeName: string } | false;
    richSnippets: { attributeName: string; };
    locales: { attributeName: string; options: string[]; };
}

export interface PastOrdersStatus {
    inProgress: boolean;
    totalOrdersCount?: number;
    collectedOrderCount?: number;
    fetchedOrderCount?: number;
    scheduledOrderCount?: number;
    reason?: number;
}

export interface PastOrdersAnalyze {
    code: number;
}
